import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Paper, Container, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Stack } from '@mui/material';
import { Menu as MenuIcon, Help as HelpIcon } from '@mui/icons-material';
import { useMaterialUIController, setMiniSidenav } from "context";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import SouthIcon from '@mui/icons-material/South';
import FilterPanelContainer from "components/Filter/Filterpopup"
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import HighlightsCards from "components/Highlights/Highlightscard";
import Visualizations from "components/Visualizations/Visualizations";
import PieLegendCard from "components/Visualizations/Piecharts"
import Map from 'components/Visualizations/Map'
import Barcharts from "components/Visualizations/Barcharts"
import LineChartSubBrand from "components/Visualizations/Linechartsubbrand";
import TopSkuBarChart from "components/Visualizations/SkuBarchart";
import DoubleBarChart from "components/Visualizations/DoubleBarcharts";
import BrandPriceDumbbellChart from "components/Visualizations/DumbleBarchart";
import datadrilldown from "assets/images/selectedicons/DataDrillDown.svg";
import drillDownArrow from "assets/images/selectedicons/DrillDownArrow.svg";
import datadrillhover from 'assets/images/selectedicons/Data Drill Down1.svg'
import FilterChip from "components/FilterChip/FilterChip";
import { fetchMyCommercialData } from "services/commercial.service";
import { getTimeSeriesLabels } from "../../../components/TimeSeries/monthUtils";
import { useFilterContext } from "../../../../src/context/FilterContext";
import { fetchCommercialFilter } from "../../../services/commercialFilter.service";

const CommercialDashboard = ({ subcategoryToken: propSubcategoryToken }) => {

  const {
    lastYearSameMonth,
    currentMonthLabel,
    prevMonthLabel
  } = getTimeSeriesLabels();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px

  // State for API data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commercialData, setCommercialData] = useState(null);

  // Filter state for Highlights section time period chips
  const [highlightsActiveIdx, setHighlightsActiveIdx] = React.useState(0);
  const [highlightsActiveChipName, setHighlightsActiveChipName] = useState("YTD");

  // Filter state for Visualizations section time period chips
  const [visualizationActiveIdx, setVisualizationActiveIdx] = React.useState(0);
  const [visualizationActiveChipName, setVisualizationActiveChipName] = useState("YTD");

  const { setFilterOptions, setFilterChips, filterOptions, setActiveFilters, activeFilters } = useFilterContext();
  const [myDashboardfilterData, setMyDashboardfilterData] = useState(null);

  useEffect(() => {
    const header = document.querySelector('.MuiAppBar-root');
    if (header) {
      header.style.display = 'none';
    }
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
      } catch (error) {
        console.error('Failed loading static mocks or transforming data:', error);
        setError('Failed to load static mock data. Check competitive_analysis/data/allMocks.json');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [location.search, propSubcategoryToken]);


  // Helper function to build GraphQL filters from activeFilters
  const buildGqlFiltersFromActive = useCallback((filters) => {
    const extract = (items) => {
      if (!items || !Array.isArray(items) || items.length === 0) return null;
      // Handle both string arrays and object arrays with name property
      const names = items.map(item => typeof item === 'string' ? item : item?.name).filter(Boolean);
      return names.length > 0 ? { in: names } : null;
    };

    const gqlFiltersRaw = {
      companyName: extract(filters?.Company),
      categoryName: extract(filters?.Category),
      brandName: extract(filters?.Brand),
      subBrandSubcategory: extract(filters?.["Sub-Brand"]),
      subBrandName: extract(filters?.["Sub-Brand"]),
      routeToMarket: extract(filters?.["Route to Market"]),
      channel: extract(filters?.Channel)
    };
    // Remove null/undefined values to avoid backend errors
    return Object.fromEntries(
      Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
    );
  }, []);

  // Fetch filter options on initial load
  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (filterOptions && Object.keys(filterOptions).length > 0) {
        return; // Already have filter options
      }

      try {
        const commercialfilterResponse = await fetchCommercialFilter();
        const filterOptionsData = {
          Company: commercialfilterResponse?.companies?.items || [],
          Category: commercialfilterResponse?.categories?.items || [],
          Brand: commercialfilterResponse?.brands?.items || [],
          "Sub-Brand": commercialfilterResponse?.subBrands?.items || [],
          "Route to Market": commercialfilterResponse?.routeToMarkets?.items || [],
          Channel: commercialfilterResponse?.channels?.items || [],
        };
        setFilterOptions(filterOptionsData);
        setMyDashboardfilterData(commercialfilterResponse);

        const companyChips = commercialfilterResponse?.companies.items || [];
        const categoryChips = commercialfilterResponse?.categories?.items || [];
        setFilterChips([
          { key: "all", name: "All" },
          ...companyChips,
          ...categoryChips,
          { key: "custom", name: "Custom" }
        ]);
      } catch (error) {
        console.error('Failed loading filter options:', error);
      }
    };

    fetchFilterOptions();
  }, [filterOptions, setFilterOptions, setFilterChips]);

  // Fetch dashboard data when filters or time periods change
  useEffect(() => {
    const fetchAllData = async () => {
      // Wait for filter options to be available if they're not loaded yet
      if (!filterOptions || Object.keys(filterOptions).length === 0) {
        return;
      }

      try {
        setLoading(true);

        // Build GraphQL filters from activeFilters if they exist and have selections
        // Otherwise, use all available filter options
        let gqlFilters = {};

        if (activeFilters && Object.values(activeFilters).some(arr => (arr || []).length > 0)) {
          // Use activeFilters when they have selections
          gqlFilters = buildGqlFiltersFromActive(activeFilters);
        } else {
          // Fallback to all filter options if no active filters
          const extractNames = (items) => {
            if (!items?.length) return null;
            return {
              in: items.map(item => typeof item === 'string' ? item : item?.name).filter(Boolean)
            };
          };

          const gqlFiltersRaw = {
            companyName: extractNames(filterOptions.Company),
            categoryName: extractNames(filterOptions.Category),
            brandName: extractNames(filterOptions.Brand),
            subBrandSubcategory: extractNames(filterOptions["Sub-Brand"]),
            subBrandName: extractNames(filterOptions?.["Sub-Brand"]),
            routeToMarket: extractNames(filterOptions["Route to Market"]),
            channel: extractNames(filterOptions.Channel)
          };
          gqlFilters = Object.fromEntries(
            Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
          );
        }

        console.log('Fetching dashboard data with filters:', gqlFilters);
        const commercialResponse = await fetchMyCommercialData(
          visualizationActiveChipName,
          highlightsActiveChipName,
          gqlFilters
        );

        const commercialItem = commercialResponse || null;
        setCommercialData(commercialItem);

      } catch (error) {
        console.error('Failed loading commercial data:', error);
        setError('Failed to load commercial data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [location.search, propSubcategoryToken, visualizationActiveChipName, highlightsActiveChipName, activeFilters, buildGqlFiltersFromActive, filterOptions]);

  const formatToMillions = (value) => {
    return value == null || value === "-"
      ? "-"
      : (Number(value) / 1_000_000).toFixed(1);
  };

  const truncateDecimal = (num, decimals = 1) => {
    const factor = Math.pow(10, decimals);
    return Math.floor(num * factor) / factor;
  };

  const normalizePrice = (value) =>
    value != null ? value / 40 : 0;

  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 65%, 50%)`;
  };

  // first section KPIs card 
  const kpicardsdata = [
    {
      "title": "Rank in Market (Dollars Sold)",
      "values": [
        {
          "label": "MTD",
          "change": commercialData?.rankInMarket?.items?.cMvsGoal ? commercialData?.rankInMarket?.items?.cMvsGoal : "0",
          "changely": commercialData?.rankInMarket?.items?.cMvsLM ? `${commercialData?.rankInMarket?.items?.cMvsLM}` : "0",
          "value": commercialData?.rankInMarket?.items?.currentMonth ? `#${commercialData?.rankInMarket?.items?.currentMonth}` : "0",
          "status": "increase",
          "title": "Month",
          "lyValue": commercialData?.rankInMarket?.items?.lastMonth ? `${commercialData?.rankInMarket?.items?.lastMonth}%` : "0%",
          "glvalue": commercialData?.rankInMarket?.items?.goalCurrentMonth ? `${commercialData?.rankInMarket?.items?.goalCurrentMonth}%` : "0%"
        },
        {
          "label": "QTD",
          "change": commercialData?.rankInMarket?.items?.cQvsGoal ? commercialData?.rankInMarket?.items?.cQvsGoal : "0",
          "changely": commercialData?.rankInMarket?.items?.cQvsLQ ? `${commercialData?.rankInMarket?.items?.cQvsLQ}` : "0",
          "value": commercialData?.rankInMarket?.items?.currentQuarter ? `#${commercialData?.rankInMarket?.items?.currentQuarter}` : "0",
          "status": "increase",
          "title": "Quarter",
          "lyValue": commercialData?.rankInMarket?.items?.lastQuarter ? `${commercialData?.rankInMarket?.items?.lastQuarter}%` : "0%",
          "glvalue": commercialData?.rankInMarket?.items?.goalCurrentQuarter ? `${commercialData?.rankInMarket?.items?.goalCurrentQuarter}%` : "0%"
        },
        {
          "label": "YTD",
          "change": commercialData?.rankInMarket?.items?.cYvsGoal ? commercialData?.rankInMarket?.items?.cYvsGoal : "0",
          "changely": commercialData?.rankInMarket?.items?.cYvsLY ? `${commercialData?.rankInMarket?.items?.cYvsLY}` : "0",
          "value": commercialData?.rankInMarket?.items?.currentYear ? `#${commercialData?.rankInMarket?.items?.currentYear}` : "0",
          "status": "increase",
          "title": "Year",
          "lyValue": commercialData?.rankInMarket?.items?.lastYear ? `${commercialData?.rankInMarket?.items?.lastYear}%` : "0%",
          "glvalue": commercialData?.rankInMarket?.items?.goalCurrentYear ? `${commercialData?.rankInMarket?.items?.goalCurrentYear}%` : "0%"
        }
      ]
    },
    {
      "title": "Market Share % (Dollars Sold)",
      "values": [
        {
          "label": "MTD",
          "change": commercialData?.marketShare?.items?.cYvsGoal ? `${commercialData?.marketShare?.items?.cYvsGoal}%` : "0%",
          "changely": commercialData?.marketShare?.items?.cMvsLM ? `${commercialData?.marketShare?.items?.cMvsLM}%` : "0%",
          "value": `${commercialData?.marketShare?.items?.currentMonth}%`,
          "status": "increase",
          "title": "Month",
          "lyValue": commercialData?.marketShare?.items?.lastMonth ? `${commercialData?.marketShare?.items?.lastMonth}%` : "0%",
          "glvalue": commercialData?.marketShare?.items?.goalCurrentMonth ? `${commercialData?.marketShare?.items?.goalCurrentMonth}%` : "0%"
        },
        {
          "label": "QTD",
          "change": commercialData?.marketShare?.items?.cQvsGoal ? `${commercialData?.marketShare?.items?.cQvsGoal}%` : "0%",
          "changely": commercialData?.marketShare?.items?.cQvsLQ ? `${commercialData?.marketShare?.items?.cQvsLQ}%` : "0%",
          "value": `${commercialData?.marketShare?.items?.currentQuarter}%`,
          "status": "increase",
          "title": "Quarter",
          "lyValue": commercialData?.marketShare?.items?.lastQuarter ? `${commercialData?.marketShare?.items?.lastQuarter}%` : "0%",
          "glvalue": commercialData?.marketShare?.items?.goalCurrentQuarter ? `${commercialData?.marketShare?.items?.goalCurrentQuarter}%` : "0%"
        },
        {
          "label": "YTD",
          "change": commercialData?.marketShare?.items?.cYvsGoal ? `${commercialData?.marketShare?.items?.cYvsGoal}%` : "0%",
          "changely": commercialData?.marketShare?.items?.cYvsLY ? `${commercialData?.marketShare?.items?.cYvsLY}%` : "0%",
          "value": `${commercialData?.marketShare?.items?.currentYear}%`,
          "status": "increase",
          "title": "Year",
          "lyValue": commercialData?.marketShare?.items?.lastYear ? `${commercialData?.marketShare?.items?.lastYear}%` : "0%",
          "glvalue": commercialData?.marketShare?.items?.goalCurrentYear ? `${commercialData?.marketShare?.items?.goalCurrentYear}%` : "0%"
        }
      ]
    },
    {
      "title": "Reported Dollars Sold",
      "values": [
        {
          "label": "MTD",
          "change": commercialData?.kpiReportedDollarSold?.items?.cYvsGoal ? `${commercialData?.kpiReportedDollarSold?.items?.cYvsGoal}%` : "0%",
          "changely": commercialData?.kpiReportedDollarSold?.items?.cMvsLM ? `${commercialData?.kpiReportedDollarSold?.items?.cMvsLM}%` : "0%",
          "value": `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.currentMonth)}M`,
          "status": "increase",
          "title": "Month",
          "lyValue": `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.lastMonth)}M`,
          "glvalue": `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.goalCurrentMonth)}M`
        },
        {
          "label": "QTD",
          "change": commercialData?.kpiReportedDollarSold?.items?.cQvsGoal ? `${commercialData?.kpiReportedDollarSold?.items?.cQvsGoal}%` : "0%",
          "changely": commercialData?.kpiReportedDollarSold?.items?.cQvsLQ ? `${commercialData?.kpiReportedDollarSold?.items?.cQvsLQ}%` : "0%",
          "value": `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.currentQuarter)}M`,
          "status": "increase",
          "title": "Quarter",
          "lyValue": `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.lastQuarter)}M`,
          "glvalue": `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.goalCurrentQuarter)}M`
        },
        {
          "label": "YTD",
          "change": commercialData?.kpiReportedDollarSold?.items?.cYvsGoal ? `${commercialData?.kpiReportedDollarSold?.items?.cYvsGoal}%` : "0%",
          "changely": commercialData?.kpiReportedDollarSold?.items?.cYvsLY ? `${commercialData?.kpiReportedDollarSold?.items?.cYvsLY}%` : "0%",
          "value": `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.currentYear)}M`,
          "status": "increase",
          "title": "Year",
          "lyValue": `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.lastYear)}M`,
          "glvalue": `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      "title": "Sales Growth Rate (Dollars Sold)",
      "values": [
        {
          "label": "MTD",
          "change": commercialData?.salesGrowthRateDollarsSold?.items?.cMvsGoal ? `${commercialData?.salesGrowthRateDollarsSold?.items?.cMvsGoal}%` : "0%",
          "changely": commercialData?.salesGrowthRateDollarsSold?.items?.cMvsLM ? `${commercialData?.salesGrowthRateDollarsSold?.items?.cMvsLM}%` : "0%",
          "value": `${truncateDecimal(commercialData?.salesGrowthRateDollarsSold?.items?.currentMonth)}%`,
          "status": "increase",
          "title": "Month",
          "lyValue": commercialData?.salesGrowthRateDollarsSold?.items?.lastMonth ? `${commercialData?.salesGrowthRateDollarsSold?.items?.lastMonth}%` : "0%",
          "glvalue": commercialData?.salesGrowthRateDollarsSold?.items?.goalCurrentMonth ? `${commercialData?.salesGrowthRateDollarsSold?.items?.goalCurrentMonth}%` : "0%"
        },
        {
          "label": "QTD",
          "change": commercialData?.salesGrowthRateDollarsSold?.items?.cQvsGoal ? `${commercialData?.salesGrowthRateDollarsSold?.items?.cQvsGoal}%` : "0%",
          "changely": commercialData?.salesGrowthRateDollarsSold?.items?.cQvsLQ ? `${commercialData?.salesGrowthRateDollarsSold?.items?.cQvsLQ}%` : "0%",
          "value": `${truncateDecimal(commercialData?.salesGrowthRateDollarsSold?.items?.currentQuarter)}%`,
          "status": "increase",
          "title": "Quarter",
          "lyValue": commercialData?.salesGrowthRateDollarsSold?.items?.lastQuarter ? `${commercialData?.salesGrowthRateDollarsSold?.items?.lastQuarter}%` : "0%",
          "glvalue": commercialData?.salesGrowthRateDollarsSold?.items?.goalCurrentQuarter ? `${commercialData?.salesGrowthRateDollarsSold?.items?.goalCurrentQuarter}%` : "0%",
        },
        {
          "label": "YTD",
          "change": commercialData?.salesGrowthRateDollarsSold?.items?.cYvsGoal ? `${commercialData?.salesGrowthRateDollarsSold?.items?.cYvsGoal}%` : "0%",
          "changely": commercialData?.salesGrowthRateDollarsSold?.items?.cYvsLY ? `${commercialData?.salesGrowthRateDollarsSold?.items?.cYvsLY}%` : "0%",
          "value": `${truncateDecimal(commercialData?.salesGrowthRateDollarsSold?.items?.currentYear)}%`,
          "status": "increase",
          "title": "Year",
          "lyValue": commercialData?.salesGrowthRateDollarsSold?.items?.lastYear ? `${commercialData?.salesGrowthRateDollarsSold?.items?.lastYear}%` : "0%",
          "glvalue": commercialData?.salesGrowthRateDollarsSold?.items?.goalCurrentYear ? `${commercialData?.salesGrowthRateDollarsSold?.items?.goalCurrentYear}%` : "0%",
        }
      ]
    },
    {
      "title": "ACV (All Commodity Volume)",
      "values": [
        {
          "label": "MTD",
          "change": commercialData?.allCommodityVolume?.items?.cMvsGoal ? `${commercialData?.allCommodityVolume?.items?.cMvsGoal}%` : "0%",
          "changely": commercialData?.allCommodityVolume?.items?.cMvsLM ? `${commercialData?.allCommodityVolume?.items?.cMvsLM}%` : "0%",
          "value": `$${formatToMillions(commercialData?.allCommodityVolume?.items?.currentMonth)}M`,
          "status": "increase",
          "title": "Month",
          "lyValue": `$${formatToMillions(commercialData?.allCommodityVolume?.items?.lastMonth)}M`,
          "glvalue": `$${formatToMillions(commercialData?.allCommodityVolume?.items?.goalCurrentMonth)}M`
        },
        {
          "label": "QTD",
          "change": commercialData?.allCommodityVolume?.items?.cQvsGoal ? `${commercialData?.allCommodityVolume?.items?.cQvsGoal}%` : "0%",
          "changely": commercialData?.allCommodityVolume?.items?.cQvsLQ ? `${commercialData?.allCommodityVolume?.items?.cQvsLQ}%` : "0%",
          "value": `$${formatToMillions(commercialData?.allCommodityVolume?.items?.currentQuarter)}M`,
          "status": "increase",
          "title": "Quarter",
          "lyValue": `$${formatToMillions(commercialData?.allCommodityVolume?.items?.lastQuarter)}M`,
          "glvalue": `$${formatToMillions(commercialData?.allCommodityVolume?.items?.goalCurrentQuarter)}M`
        },
        {
          "label": "YTD",
          "change": commercialData?.allCommodityVolume?.items?.cYvsGoal ? `${commercialData?.allCommodityVolume?.items?.cYvsGoal}%` : "0%",
          "changely": commercialData?.allCommodityVolume?.items?.cYvsLY ? `${commercialData?.allCommodityVolume?.items?.cYvsLY}%` : "0%",
          "value": `$${formatToMillions(commercialData?.allCommodityVolume?.items?.currentYear)}M`,
          "status": "increase",
          "title": "Year",
          "lyValue": `$${formatToMillions(commercialData?.allCommodityVolume?.items?.lastYear)}M`,
          "glvalue": `$${formatToMillions(commercialData?.allCommodityVolume?.items?.goalCurrentYear)}M`
        }
      ]
    },
    {
      "title": "Average Price per Unit",
      "values": [
        {
          "label": "MTD",
          "change": commercialData?.averagePricePerUnit?.items?.cMvsGoal ? `${commercialData?.averagePricePerUnit?.items?.cMvsGoal}%` : "0%",
          "changely": commercialData?.averagePricePerUnit?.items?.cMvsLM ? `${commercialData?.averagePricePerUnit?.items?.cMvsLM}%` : "0%",
          "value": `$${truncateDecimal(commercialData?.averagePricePerUnit?.items?.currentMonth)}`,
          "status": "increase",
          "title": "Month",
          "lyValue": `$${truncateDecimal(commercialData?.averagePricePerUnit?.items?.lastMonth)}`,
          "glvalue": `$${commercialData?.averagePricePerUnit?.items?.goalCurrentMonth}`
        },
        {
          "label": "QTD",
          "change": commercialData?.averagePricePerUnit?.items?.cQvsGoal ? `${commercialData?.averagePricePerUnit?.items?.cQvsGoal}%` : "0%",
          "changely": commercialData?.averagePricePerUnit?.items?.cQvsLQ ? `${commercialData?.averagePricePerUnit?.items?.cQvsLQ}%` : "0%",
          "value": `$${truncateDecimal(commercialData?.averagePricePerUnit?.items?.currentQuarter)}`,
          "status": "increase",
          "title": "Quarter",
          "lyValue": `$${truncateDecimal(commercialData?.averagePricePerUnit?.items?.lastQuarter)}`,
          "glvalue": `$${commercialData?.averagePricePerUnit?.items?.goalCurrentQuarter}`
        },
        {
          "label": "YTD",
          "change": commercialData?.averagePricePerUnit?.items?.cYvsGoal ? `${commercialData?.averagePricePerUnit?.items?.cYvsGoal}%` : "0%",
          "changely": commercialData?.averagePricePerUnit?.items?.cYvsLY ? `${commercialData?.averagePricePerUnit?.items?.cYvsLY}%` : "0%",
          "value": `$${truncateDecimal(commercialData?.averagePricePerUnit?.items?.currentYear)}`,
          "status": "increase",
          "title": "Year",
          "lyValue": `$${truncateDecimal(commercialData?.averagePricePerUnit?.items?.lastYear)}`,
          "glvalue": `$${commercialData?.averagePricePerUnit?.items?.goalCurrentYear}`
        }
      ]
    },
    {
      "title": "% Dollars Sold on Promotion",
      "values": [
        {
          "label": "MTD",
          "change": commercialData?.percentageDollarsSoldOnPromotion?.items?.cMvsGoal ? `${commercialData?.percentageDollarsSoldOnPromotion?.items?.cMvsGoal}%` : "0%",
          "changely": commercialData?.percentageDollarsSoldOnPromotion?.items?.cMvsLM ? `${commercialData?.percentageDollarsSoldOnPromotion?.items?.cMvsLM}%` : "0%",
          "value": `${commercialData?.percentageDollarsSoldOnPromotion?.items?.currentMonth}%`,
          "status": "increase",
          "title": "Month",
          "lyValue": `${commercialData?.percentageDollarsSoldOnPromotion?.items?.lastMonth}%`,
          "glvalue": `${commercialData?.percentageDollarsSoldOnPromotion?.items?.goalCurrentMonth}%`
        },
        {
          "label": "QTD",
          "change": commercialData?.percentageDollarsSoldOnPromotion?.items?.cQvsGoal ? `${commercialData?.percentageDollarsSoldOnPromotion?.items?.cQvsGoal}%` : "0%",
          "changely": commercialData?.percentageDollarsSoldOnPromotion?.items?.cQvsLQ ? `${commercialData?.percentageDollarsSoldOnPromotion?.items?.cQvsLQ}%` : "0%",
          "value": `${commercialData?.percentageDollarsSoldOnPromotion?.items?.currentQuarter}%`,
          "status": "increase",
          "title": "Quarter",
          "lyValue": `${commercialData?.percentageDollarsSoldOnPromotion?.items?.lastQuarter}%`,
          "glvalue": `${commercialData?.percentageDollarsSoldOnPromotion?.items?.goalCurrentQuarter}%`,
        },
        {
          "label": "YTD",
          "change": commercialData?.percentageDollarsSoldOnPromotion?.items?.cYvsGoal ? `${commercialData?.percentageDollarsSoldOnPromotion?.items?.cYvsGoal}%` : "0%",
          "changely": commercialData?.percentageDollarsSoldOnPromotion?.items?.cYvsLY ? `${commercialData?.percentageDollarsSoldOnPromotion?.items?.cYvsLY}%` : "0%",
          "value": `${commercialData?.percentageDollarsSoldOnPromotion?.items?.currentYear}%`,
          "status": "increase",
          "title": "Year",
          "lyValue": `${commercialData?.percentageDollarsSoldOnPromotion?.items?.lastYear}%`,
          "glvalue": `${commercialData?.percentageDollarsSoldOnPromotion?.items?.goalCurrentYear}%`,
        }
      ]
    },
    {
      "title": "% Dollars Sold on Display",
      "values": [
        {
          "label": "MTD",
          "change": commercialData?.percentageDollarsSoldOnDisplay?.items?.cMvsGoal ? `${commercialData?.percentageDollarsSoldOnDisplay?.items?.cMvsGoal}%` : "0%",
          "changely": commercialData?.percentageDollarsSoldOnDisplay?.items?.cMvsLM ? `${commercialData?.percentageDollarsSoldOnDisplay?.items?.cMvsLM}%` : "0%",
          "value": `${commercialData?.percentageDollarsSoldOnDisplay?.items?.currentMonth}%`,
          "status": "increase",
          "title": "Month",
          "lyValue": `${commercialData?.percentageDollarsSoldOnDisplay?.items?.lastMonth}%`,
          "glvalue": `${commercialData?.percentageDollarsSoldOnDisplay?.items?.goalCurrentMonth}%`
        },
        {
          "label": "QTD",
          "change": commercialData?.percentageDollarsSoldOnDisplay?.items?.cQvsGoal ? `${commercialData?.percentageDollarsSoldOnDisplay?.items?.cQvsGoal}%` : "0%",
          "changely": commercialData?.percentageDollarsSoldOnDisplay?.items?.cQvsLQ ? `${commercialData?.percentageDollarsSoldOnDisplay?.items?.cQvsLQ}%` : "0%",
          "value": `${commercialData?.percentageDollarsSoldOnDisplay?.items?.currentQuarter}%`,
          "status": "increase",
          "title": "Quarter",
          "lyValue": `${commercialData?.percentageDollarsSoldOnDisplay?.items?.lastQuarter}%`,
          "glvalue": `${commercialData?.percentageDollarsSoldOnDisplay?.items?.goalCurrentQuarter}%`,
        },
        {
          "label": "YTD",
          "change": commercialData?.percentageDollarsSoldOnDisplay?.items?.cYvsGoal ? `${commercialData?.percentageDollarsSoldOnDisplay?.items?.cYvsGoal}%` : "0%",
          "changely": commercialData?.percentageDollarsSoldOnDisplay?.items?.cYvsLY ? `${commercialData?.percentageDollarsSoldOnDisplay?.items?.cYvsLY}%` : "0%",
          "value": `${commercialData?.percentageDollarsSoldOnDisplay?.items?.currentMonth}%`,
          "status": "increase",
          "title": "Year",
          "lyValue": `${commercialData?.percentageDollarsSoldOnDisplay?.items?.lastYear}%`,
          "glvalue": `${commercialData?.percentageDollarsSoldOnDisplay?.items?.goalCurrentYear}%`,
        }
      ]
    },
    {
      "title": "Innovation Rate (Dollars Sold)",
      "values": [
        {
          "label": "MTD",
          "change": commercialData?.innovationRate?.items?.cMvsGoal ? `${commercialData?.innovationRate?.items?.cMvsGoal}%` : "0%",
          "changely": commercialData?.innovationRate?.items?.cMvsLM ? `${commercialData?.innovationRate?.items?.cMvsLM}%` : "0%",
          "value": `${commercialData?.innovationRate?.items?.currentMonth}%`,
          "status": "increase",
          "title": "Month",
          "lyValue": `${commercialData?.innovationRate?.items?.lastMonth}%`,
          "glvalue": `${commercialData?.innovationRate?.items?.goalCurrentMonth}%`
        },
        {
          "label": "QTD",
          "change": commercialData?.innovationRate?.items?.cQvsGoal ? `${commercialData?.innovationRate?.items?.cQvsGoal}%` : "0%",
          "changely": commercialData?.innovationRate?.items?.cQvsLQ ? `${commercialData?.innovationRate?.items?.cQvsLQ}%` : "0%",
          "value": `${commercialData?.innovationRate?.items?.currentQuarter}%`,
          "status": "increase",
          "title": "Quarter",
          "lyValue": `${commercialData?.innovationRate?.items?.lastQuarter}%`,
          "glvalue": `${commercialData?.innovationRate?.items?.goalCurrentQuarter}%`
        },
        {
          "label": "YTD",
          "change": commercialData?.innovationRate?.items?.cYvsGoal ? `${commercialData?.innovationRate?.items?.cYvsGoal}%` : "0%",
          "changely": commercialData?.innovationRate?.items?.cYvsLY ? `${commercialData?.innovationRate?.items?.cYvsLY}%` : "0%",
          "value": `${commercialData?.innovationRate?.items?.currentYear}%`,
          "status": "increase",
          "title": "Year",
          "lyValue": `${commercialData?.innovationRate?.items?.lastYear}%`,
          "glvalue": `${commercialData?.innovationRate?.items?.goalCurrentYear}%`,
        }
      ]
    }
  ]

  //second section time series cards
  const timeSeriesCardsdata = [
    {
      "title": "Rank in Market (Dollars Sold)",
      "values": [
        {
          label: lastYearSameMonth,
          change: commercialData?.rankInMarket?.items?.cmcYvsCMLY ? `${truncateDecimal(commercialData?.rankInMarket?.items?.cmcYvsCMLY)}` : "0",
          value: `#${commercialData?.rankInMarket?.items?.sameMonthLastYear ?? "0"}`,
          status: "",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: commercialData?.rankInMarket?.items?.currentMonth ? `#${commercialData?.rankInMarket?.items?.currentMonth}` : "0",
          status: "",
          arrow: "left"
        },
        {
          label: prevMonthLabel,
          change: commercialData?.rankInMarket?.items?.cMvsLM ? `${truncateDecimal(commercialData?.rankInMarket?.items?.cMvsLM)}` : "0",
          value: `#${commercialData?.rankInMarket?.items?.lastMonth ?? 0}`,
          status: "",
          arrow: "left"
        }
      ]
    },
    {
      title: "Market Share % (Dollars Sold)",
      values: [
        {
          label: lastYearSameMonth,
          change: commercialData?.marketShare?.items?.cmcYvsCMLY ? `${truncateDecimal(commercialData?.marketShare?.items?.cmcYvsCMLY)}` : "0",
          value: commercialData?.marketShare?.items?.sameMonthLastYear ? `${truncateDecimal(commercialData?.marketShare?.items?.sameMonthLastYear)}` : "0",
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: commercialData?.marketShare?.items?.currentMonth ? `${truncateDecimal(commercialData?.marketShare?.items?.currentMonth)}` : "0",
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: commercialData?.marketShare?.items?.cMvsLM ? `${truncateDecimal(commercialData?.marketShare?.items?.cMvsLM)}` : "0",
          value: commercialData?.marketShare?.items?.lastMonth ? `${truncateDecimal(commercialData?.marketShare?.items?.lastMonth)}` : "0",
          status: "decrease",
          arrow: "left"
        }
      ]
    },
    {
      title: "Reported Dollars Sold",
      values: [
        {
          label: lastYearSameMonth,
          change: commercialData?.kpiReportedDollarSold?.items?.cmcYvsCMLY ? `${truncateDecimal(commercialData?.kpiReportedDollarSold?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.sameMonthLastYear)}`,
          status: "decrease",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.currentMonth)}`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: commercialData?.kpiReportedDollarSold?.items?.cMvsLM ? `${truncateDecimal(commercialData?.kpiReportedDollarSold?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(commercialData?.kpiReportedDollarSold?.items?.lastMonth)}`,
          status: "decrease",
          arrow: "left"
        }
      ]
    },
    {
      title: "Sales Growth Rate (Dollars Sold)",
      values: [
        {
          label: lastYearSameMonth,
          change: commercialData?.salesGrowthRateDollarsSold?.items?.cmcYvsCMLY ? `${truncateDecimal(commercialData?.salesGrowthRateDollarsSold?.items?.cmcYvsCMLY)}%` : "0%",
          value: commercialData?.salesGrowthRateDollarsSold?.items?.sameMonthLastYear ? `${truncateDecimal(commercialData?.salesGrowthRateDollarsSold?.items?.sameMonthLastYear)}%` : "0%",
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: commercialData?.salesGrowthRateDollarsSold?.items?.currentMonth ? `${truncateDecimal(commercialData?.salesGrowthRateDollarsSold?.items?.currentMonth)}%` : "0%",
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: commercialData?.salesGrowthRateDollarsSold?.items?.cMvsLM ? `${truncateDecimal(commercialData?.salesGrowthRateDollarsSold?.items?.cMvsLM)}%` : "0%",
          value: commercialData?.salesGrowthRateDollarsSold?.items?.lastMonth ? `${truncateDecimal(commercialData?.salesGrowthRateDollarsSold?.items?.lastMonth)}%` : "0%",
          status: "decrease",
          arrow: "left"
        }
      ]
    },
    {
      title: "ACV (All Commodity Volume)",
      values: [
        {
          label: lastYearSameMonth,
          change: commercialData?.allCommodityVolume?.items?.cmcYvsCMLY ? `${truncateDecimal(commercialData?.allCommodityVolume?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${formatToMillions(commercialData?.allCommodityVolume?.items?.sameMonthLastYear)}M`,
          status: "decrease",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${formatToMillions(commercialData?.allCommodityVolume?.items?.currentMonth)}M`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: commercialData?.allCommodityVolume?.items?.cMvsLM ? `${truncateDecimal(commercialData?.allCommodityVolume?.items?.cMvsLM)}%` : "0%",
          value: `$${formatToMillions(commercialData?.allCommodityVolume?.items?.lastMonth)}M`,
          status: "decrease",
          arrow: "left"
        }
      ]
    },
    {
      title: "Average Price per Unit",
      values: [
        {
          label: lastYearSameMonth,
          change: commercialData?.averagePricePerUnit?.items?.cmcYvsCMLY ? `${truncateDecimal(commercialData?.averagePricePerUnit?.items?.cmcYvsCMLY)}%` : "0%",
          value: `$${truncateDecimal(commercialData?.averagePricePerUnit?.items?.sameMonthLastYear)}`,
          status: "decrease",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: `$${truncateDecimal(commercialData?.averagePricePerUnit?.items?.currentMonth)}`,
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: commercialData?.averagePricePerUnit?.items?.cMvsLM ? `${truncateDecimal(commercialData?.averagePricePerUnit?.items?.cMvsLM)}%` : "0%",
          value: `$${truncateDecimal(commercialData?.averagePricePerUnit?.items?.lastMonth)}`,
          status: "decrease",
          arrow: "left"
        }
      ]
    },
    {
      title: "% Dollars Sold on Promotion",
      values: [
        {
          label: lastYearSameMonth,
          change: commercialData?.percentageDollarsSoldOnPromotion?.items?.cmcYvsCMLY ? `${truncateDecimal(commercialData?.percentageDollarsSoldOnPromotion?.items?.cmcYvsCMLY)}%` : "0%",
          value: commercialData?.percentageDollarsSoldOnPromotion?.items?.sameMonthLastYear ? `${truncateDecimal(commercialData?.percentageDollarsSoldOnPromotion?.items?.sameMonthLastYear)}%` : "0%",
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: commercialData?.percentageDollarsSoldOnPromotion?.items?.currentMonth ? `${truncateDecimal(commercialData?.percentageDollarsSoldOnPromotion?.items?.currentMonth)}%` : "0%",
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: commercialData?.percentageDollarsSoldOnPromotion?.items?.cMvsLM ? `${truncateDecimal(commercialData?.percentageDollarsSoldOnPromotion?.items?.cMvsLM)}%` : "0%",
          value: commercialData?.percentageDollarsSoldOnPromotion?.items?.lastMonth ? `${truncateDecimal(commercialData?.percentageDollarsSoldOnPromotion?.items?.lastMonth)}%` : "0%",
          status: "decrease",
          arrow: "left"
        }
      ]
    },
    {
      title: "% Dollars Sold on Display",
      values: [
        {
          label: lastYearSameMonth,
          change: commercialData?.percentageDollarsSoldOnDisplay?.items?.cmcYvsCMLY ? `${truncateDecimal(commercialData?.percentageDollarsSoldOnDisplay?.items?.cmcYvsCMLY)}%` : "0%",
          value: commercialData?.percentageDollarsSoldOnDisplay?.items?.sameMonthLastYear ? `${truncateDecimal(commercialData?.percentageDollarsSoldOnDisplay?.items?.sameMonthLastYear)}%` : "0%",
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: commercialData?.percentageDollarsSoldOnDisplay?.items?.currentMonth ? `${truncateDecimal(commercialData?.percentageDollarsSoldOnDisplay?.items?.currentMonth)}%` : "0%",
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: commercialData?.percentageDollarsSoldOnDisplay?.items?.cMvsLM ? `${truncateDecimal(commercialData?.percentageDollarsSoldOnDisplay?.items?.cMvsLM)}%` : "0%",
          value: commercialData?.percentageDollarsSoldOnDisplay?.items?.lastMonth ? `${truncateDecimal(commercialData?.percentageDollarsSoldOnDisplay?.items?.lastMonth)}%` : "0%",
          status: "decrease",
          arrow: "left"
        }
      ]
    },
    {
      title: "Innovation Rate (Dollars Sold)",
      values: [
        {
          label: lastYearSameMonth,
          change: commercialData?.innovationRate?.items?.cmcYvsCMLY ? `${truncateDecimal(commercialData?.innovationRate?.items?.cmcYvsCMLY)}%` : "0%",
          value: commercialData?.innovationRate?.items?.sameMonthLastYear ? `${truncateDecimal(commercialData?.innovationRate?.items?.sameMonthLastYear)}%` : "0%",
          status: "increase",
          arrow: "right"
        },
        {
          label: currentMonthLabel,
          change: "",
          value: commercialData?.innovationRate?.items?.currentMonth ? `${truncateDecimal(commercialData?.innovationRate?.items?.currentMonth)}%` : "0%",
          status: "nochange",
          arrow: null
        },
        {
          label: prevMonthLabel,
          change: commercialData?.innovationRate?.items?.cMvsLM ? `${truncateDecimal(commercialData?.innovationRate?.items?.cMvsLM)}%` : "0%",
          value: commercialData?.innovationRate?.items?.lastMonth ? `${truncateDecimal(commercialData?.innovationRate?.items?.lastMonth)}%` : "0%",
          status: "increase",
          arrow: "left"
        }
      ]
    }
  ];
  // third section highlights card 
  const highlightCards = [
    // --- Sub Brand by Dollars Sold ---
    {
      title: "#1 Sub Brand by Dollars Sold",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(commercialData?.reportedDollarSoldBySubBrand?.items[0]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(commercialData?.reportedDollarSoldBySubBrand?.items[0]?.netSales)}M`,
      bigTitle: commercialData?.reportedDollarSoldBySubBrand?.items[0]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(commercialData?.reportedDollarSoldBySubBrand?.items[0]?.lastPeriodNetSales)}M`
    },
    {
      title: "#2 Sub Brand by Dollars Sold",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(commercialData?.reportedDollarSoldBySubBrand?.items[1]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(commercialData?.reportedDollarSoldBySubBrand?.items[1]?.netSales)}M`,
      bigTitle: commercialData?.reportedDollarSoldBySubBrand?.items[1]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(commercialData?.reportedDollarSoldBySubBrand?.items[1]?.lastPeriodNetSales)}M`
    },
    {
      title: "#3 Sub Brand by Dollars Sold",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(commercialData?.reportedDollarSoldBySubBrand?.items[2]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(commercialData?.reportedDollarSoldBySubBrand?.items[2]?.netSales)}M`,
      bigTitle: commercialData?.reportedDollarSoldBySubBrand?.items[2]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(commercialData?.reportedDollarSoldBySubBrand?.items[2]?.lastPeriodNetSales)}M`
    },

    // --- Distributor by Reported Sales ---
    {
      title: "#1 Distributor by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(commercialData?.distributorByReportedSales?.items[0]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(commercialData?.distributorByReportedSales?.items[0]?.netSales)}M`,
      bigTitle: commercialData?.distributorByReportedSales?.items[0]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(commercialData?.distributorByReportedSales?.items[0]?.lastPeriodNetSales)}M`
    },
    {
      title: "#2 Distributor by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(commercialData?.distributorByReportedSales?.items[1]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(commercialData?.distributorByReportedSales?.items[1]?.netSales)}M`,
      bigTitle: commercialData?.distributorByReportedSales?.items[1]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(commercialData?.distributorByReportedSales?.items[1]?.lastPeriodNetSales)}M`
    },
    {
      title: "#3 Distributor by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(commercialData?.distributorByReportedSales?.items[2]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(commercialData?.distributorByReportedSales?.items[2]?.netSales)}M`,
      bigTitle: commercialData?.distributorByReportedSales?.items[2]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(commercialData?.distributorByReportedSales?.items[2]?.lastPeriodNetSales)}M`
    },

    // --- Retailer by Reported Sales ---
    {
      title: "#1 Retailer by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(commercialData?.retailerByReportedSales?.items[0]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(commercialData?.retailerByReportedSales?.items[0]?.netSales)}M`,
      bigTitle: commercialData?.retailerByReportedSales?.items[0]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(commercialData?.retailerByReportedSales?.items[0]?.lastPeriodNetSales)}M`
    },
    {
      title: "#2 Retailer by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(commercialData?.retailerByReportedSales?.items[1]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(commercialData?.retailerByReportedSales?.items[1]?.netSales)}M`,
      bigTitle: commercialData?.retailerByReportedSales?.items[1]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(commercialData?.retailerByReportedSales?.items[1]?.lastPeriodNetSales)}M`
    },
    {
      title: "#3 Retailer by Reported Sales",
      topLeftLabel: "UNITS SOLD",
      topLeftValue: `$${formatToMillions(commercialData?.retailerByReportedSales?.items[2]?.unitSold)}M`,
      topRightLabel: "NET SALES",
      topRightValue: `$${formatToMillions(commercialData?.retailerByReportedSales?.items[2]?.netSales)}M`,
      bigTitle: commercialData?.retailerByReportedSales?.items[2]?.name,
      bottomLabel: "UNITS SOLD LY",
      bottomValue: `$${formatToMillions(commercialData?.retailerByReportedSales?.items[2]?.lastPeriodNetSales)}M`
    }
  ];


  const [openFilters, setOpenFilters] = useState(false);
  const handleToggleFilters = () => {
    setOpenFilters((prev) => !prev);
  };

  // Filter state
  const [activeFilter, setActiveFilter] = useState('All');
  const [filterLoading, setFilterLoading] = useState(false);

  // Handler for Highlights section chips
  const handleHighlightsChipClick = (idx, label) => {
    setHighlightsActiveIdx(idx);
    setHighlightsActiveChipName(label);
    console.log("Clicked Highlights Chip Name:", label, "Index:", idx);
  };

  // Handler for Visualizations section chips
  const handleVisualizationChipClick = (idx, label) => {
    setVisualizationActiveIdx(idx);
    setVisualizationActiveChipName(label);
    console.log("Clicked Visualization Chip Name:", label, "Index:", idx);
  };

  // Define filter presets for each quick filter
  // Define filter presets for each quick filter
  const getFilterPreset = useCallback((filterName) => {
    if (!filterOptions) return null;

    // Helper to extract names from filter options
    const getNames = (key) => (filterOptions[key] || []).map(v => v?.name || v);

    // Get all available options dynamically
    const allCompanies = getNames("Company");
    const allCategories = getNames("Category");
    const allBrands = getNames("Brand");
    const allSubBrands = getNames("Sub-Brand");
    const allRoutes = getNames("Route to Market");
    const allChannels = getNames("Channel");

    // Helper to get Nutrabolt brands/sub-brands (exclude Bloom)
    const getNutraboltBrands = () => {
      return allBrands
        .filter(brand => {
          const brandName = typeof brand === 'string' ? brand : brand?.name;
          return brandName && !brandName.toLowerCase().includes('bloom');
        })
        .map(b => typeof b === 'string' ? b : b?.name);
    };

    const getNutraboltSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          return subBrandName && !subBrandName.toLowerCase().includes('bloom');
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to get Bloom brands/sub-brands
    const getBloomBrands = () => {
      return allBrands
        .filter(brand => {
          const brandName = typeof brand === 'string' ? brand : brand?.name;
          return brandName && brandName.toLowerCase().includes('bloom');
        })
        .map(b => typeof b === 'string' ? b : b?.name);
    };

    const getBloomSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          return subBrandName && subBrandName.toLowerCase().includes('bloom');
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to get Functional Beverage sub-brands (beverage products, exclude powders)
    const getFunctionalBeverageSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          // Exclude powder/supplement sub-brands
          return subBrandName &&
            !subBrandName.toLowerCase().includes('powder') &&
            !subBrandName.toLowerCase().includes('cellucor') &&
            subBrandName !== 'XTEND' &&
            subBrandName !== 'C4';
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to get Active Nutrition sub-brands (supplements/powders)
    const getActiveNutritionSubBrands = () => {
      return allSubBrands
        .filter(subBrand => {
          const subBrandName = typeof subBrand === 'string' ? subBrand : subBrand?.name;
          // Include powder and supplement sub-brands
          return subBrandName && (
            subBrandName.toLowerCase().includes('powder') ||
            subBrandName === 'XTEND' ||
            subBrandName === 'C4' ||
            subBrandName.toLowerCase().includes('cellucor')
          );
        })
        .map(sb => typeof sb === 'string' ? sb : sb?.name);
    };

    // Helper to find category by name
    const findCategory = (searchTerm) => {
      return allCategories.find(cat => {
        const catName = typeof cat === 'string' ? cat : cat?.name;
        return catName && catName.toLowerCase().includes(searchTerm.toLowerCase());
      });
    };

    switch (filterName) {
      case 'All':
        return {
          Company: allCompanies,
          Category: allCategories,
          Brand: allBrands,
          "Sub-Brand": allSubBrands,
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Nutrabolt':
        return {
          Company: ["Nutrabolt"],
          Category: allCategories,
          Brand: getNutraboltBrands(),
          "Sub-Brand": getNutraboltSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Bloom':
        const bloomCategory = findCategory('functional');
        return {
          Company: ["Bloom"],
          Category: bloomCategory ? [typeof bloomCategory === 'string' ? bloomCategory : bloomCategory?.name] : [],
          Brand: getBloomBrands(),
          "Sub-Brand": getBloomSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Functional Beverage':
        const functionalBeverageCategory = findCategory('functional');
        const functionalBeverageBrands = allBrands
          .filter(brand => {
            const brandName = typeof brand === 'string' ? brand : brand?.name;
            return brandName && (brandName === 'C4' || brandName.toLowerCase().includes('bloom'));
          })
          .map(b => typeof b === 'string' ? b : b?.name);
        return {
          Company: allCompanies,
          Category: functionalBeverageCategory ? [typeof functionalBeverageCategory === 'string' ? functionalBeverageCategory : functionalBeverageCategory?.name] : [],
          Brand: functionalBeverageBrands,
          "Sub-Brand": getFunctionalBeverageSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      case 'Active Nutrition':
        const activeNutritionCategory = findCategory('active');
        return {
          Company: ["Nutrabolt"],
          Category: activeNutritionCategory ? [typeof activeNutritionCategory === 'string' ? activeNutritionCategory : activeNutritionCategory?.name] : [],
          Brand: getNutraboltBrands(),
          "Sub-Brand": getActiveNutritionSubBrands(),
          "Route to Market": allRoutes,
          Channel: allChannels,
        };
      default:
        return null;
    }
  }, [filterOptions]);



  const [filterPresets, setFilterPresets] = useState(() => getFilterPreset('All'));
  useEffect(() => {
    if (filterOptions && !filterPresets) {
      const allPreset = getFilterPreset('All');
      if (allPreset) {
        setFilterPresets(allPreset);
      }
    }
  }, [filterOptions, filterPresets, getFilterPreset]);
  // Handle filter click with full-page loader
  const handleFilterClick = (filter) => {
    if (filter === activeFilter || filterLoading) return;

    setFilterLoading(true);
    setLoading(true); // Show full-page loader

    // Simulate loading delay
    setTimeout(() => {
      const preset = getFilterPreset(filter);
      setActiveFilter(filter);

      // Set the preset - this will trigger FilterPanelContainer to apply it
      if (preset) {
        setFilterPresets(preset);
      } else {
        setFilterPresets(null);
      }

      setFilterLoading(false);
      setLoading(false); // Hide full-page loader

      // Open filter panel for "Custom" filter
      if (filter === 'Custom') {
        setOpenFilters(true);
      }
    }, 800);
  };

  // Handle filter changes from FilterPanelContainer
  const handleFiltersChange = useCallback((newFilters) => {
    // Don't check for preset match if we're already in Custom mode
    // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
    if (activeFilter === 'Custom') return;

    // Check if the current filters match any preset
    const presetNames = ['All', 'Nutrabolt', 'Bloom', 'Functional Beverage', 'Active Nutrition'];
    let matchedPreset = null;

    for (const presetName of presetNames) {
      const preset = getFilterPreset(presetName);
      if (preset) {
        const isMatching = Object.keys(preset).every(key => {
          const presetValues = [...(preset[key] || [])].sort();
          const currentValues = [...(newFilters[key] || [])].sort();
          return presetValues.length === currentValues.length &&
            presetValues.every((val, idx) => val === currentValues[idx]);
        });

        if (isMatching) {
          matchedPreset = presetName;
          break;
        }
      }
    }

    // If filters don't match any preset, switch to Custom
    if (!matchedPreset && activeFilter !== 'Custom') {
      setActiveFilter('Custom');
      setFilterPresets(null);
    }
  }, [activeFilter, getFilterPreset]);

  const kpiBriefData = [
    {
      title: "Month",
      value: "$ 65.2 M",
      lyValue: "59.12 M",
      pct: "5.8%",
      positive: true,
      status: "increase",
      glvalue: "$ 85.5 M"
    },
    {
      title: "Quarter",
      value: "$ 180.6 M",
      lyValue: "160.29 M",
      pct: "11.9%",
      positive: true,
      status: "increase",
      glvalue: "$ 207.3 M"
    },
    {
      title: "Year",
      value: "$ 859.1 M",
      lyValue: "823.03 M",
      pct: "5.6%",
      positive: true,
      status: "increase",
      glvalue: "$ 882.8 M"
    }
  ];

  const getBrandColor = (label) => {
    const colorMap = {
      "Cellucor": "#747474",
      "XTEND": "#C5C5C5",
      "Bloom": "#C393E4",
      "C4": "#ECD01C",
    };

    return colorMap[label] || "#000000"; // fallback color
  };

  const getSubBrandColor = (label) => {
    const colorMap = {
      "C4 Powder": "#C2CF2D",
      "Bloom Pop": "#59A671",
      "Bloom Sparkling Energy": "#C393E4",
      "XTEND": "#C5C5C5",
      "Cellucor": "#747474",
      "C4 Ultimate": "#1A1A1A",
      "C4 Smart Energy": "#1BA4E6",
      "C4 Performance": "#ECD01C",
    };

    return colorMap[label] || "#000000"; // fallback color
  };

  const brandsData = useMemo(() => {
    const items = commercialData?.profitabilitySoldByByBrand?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      label: item.name || "",
      value: Number(item.percentage?.toFixed(1)) || 0,
      color: getBrandColor(item.name),
    }));
  }, [commercialData]);

  const subbrandsData = useMemo(() => {
    const items = commercialData?.reportedDollarSoldBySubBrand?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      label: item.name || "",
      value: Number(item.percentage?.toFixed(1)) || 0,
      color: getSubBrandColor(item.name),
    }));
  }, [commercialData]);



  const getCompetitorColor = (competitorName) => {
    if (!competitorName) return "#000000";
    console.log(competitorName, "&&&")
    const colorMap = {
      Monster: "#95C11F",
      Celsius: "#F37021",
      Alani: "#00C3DA",
      Ghost: "#F9005A",
      Rockstar: "#FFB300",
      NOS: "#0066CC",
      C4: "#ECD01C",
      Bloom: "#C393E4",
      Others: "#000000",
      "Red Bull": "#DA291C",
    };

    return colorMap[competitorName.trim()] || "#000000";
  };
  const pieDataCompetitors = useMemo(() => {
    const items = commercialData?.brandMarketShareByDollarSold?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      label: item.brandName || "",
      value: Number(item.percentage?.toFixed(1)) || 0,
      color: getCompetitorColor(item.brandName),
    }));
  }, [commercialData]);


  const getRegionColor = (regionName) => {
    if (!regionName) return "#000000";

    const colorMap = {
      West: "#4BB056",
      North: "#BFD3EA",
      South: "#9962CC",
      "South Central": "#F0C287",
      Northeast: "#E15242",
      Central: "#DBD88B",
    };

    return colorMap[regionName.trim()] || "#000000";
  };

  const pieDataRegion = useMemo(() => {
    const items = commercialData?.reportedDollarSoldByRegion?.items;

    if (!Array.isArray(items) || items.length === 0) return [];

    return items.map((item) => ({
      label: item.name || "",
      value: Number(item.percentage?.toFixed(1)) || 0,
      color: getRegionColor(item.name),
    }));
  }, [commercialData]);



  const regionChartData = useMemo(() => {
    const items =
      commercialData?.reportedDollarSoldByRegionBySubBrand?.items;

    if (!Array.isArray(items) || items.length === 0) return null;

    // ---- X Labels (Regions)
    const xLabels = items.map(item => item.region);

    // ---- Collect ALL unique brands across regions
    const brandSet = new Set();
    items.forEach(item => {
      item.values?.forEach(v => {
        brandSet.add(v.subBrandName);
      });
    });

    const brands = Array.from(brandSet);

    // ---- Build Series using your color function
    const series = brands.map(brand => ({
      label: brand,
      color: getSubBrandColor(brand)
    }));

    // ---- Build Matrix (correct mapping from API)
    const actual = items.map(regionItem => {
      return brands.map(brand => {
        const found = regionItem.values?.find(
          v => v.subBrandName === brand
        );
        return found ? Number(found.salesAmount) / 1000 : 0;
      });
    });


    const stackedTotals = actual.map(row =>
      row.reduce((sum, val) => sum + val, 0)
    );

    // Get max stacked value (true bar height)
    const maxValue = Math.max(...stackedTotals, 0);
    const niceTickStep = (maxVal, tickCount = 5) => {
      if (maxVal === 0) return 1;

      const roughStep = maxVal / tickCount;
      const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
      const residual = roughStep / magnitude;

      let niceResidual;
      if (residual >= 5) niceResidual = 5;
      else if (residual >= 2) niceResidual = 2;
      else niceResidual = 1;

      return niceResidual * magnitude;
    };

    const tickStep = niceTickStep(maxValue, 5);
    const maxTick = tickStep * Math.ceil(maxValue / tickStep);

    const yTicks = [];
    for (let tick = 0; tick <= maxTick + tickStep / 2; tick += tickStep) {
      yTicks.push(Number(tick.toFixed(2)));
    }

    return {
      title: "Reported Dollar Sales by Region",
      xLabels,
      series,
      dataMatrix: actual,   // NOW MATCHES STATIC SAMPLE
      yTicks
    };
  }, [commercialData]);



  console.log(regionChartData, "regionChartDatas")


  const lineChartSubBrandData = useMemo(() => {
    const items =
      commercialData?.reportedDollarsSoldMonthlyBySubBrand?.items;

    if (!Array.isArray(items) || items.length === 0) return null;

    // ----------------------------
    // Month order for sorting
    const monthOrder = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const sortedItems = [...items].sort((a, b) => {
      const [mA, yA] = a.month.split("-");
      const [mB, yB] = b.month.split("-");
      return new Date(yA, monthOrder.indexOf(mA)) -
        new Date(yB, monthOrder.indexOf(mB));
    });

    // Format X Labels
    const xLabels = sortedItems.map(item => {
      const [m, y] = item.month.split("-");
      return `${m}. ${y}`;
    });

    // Collect unique sub-brands
    const subBrandSet = new Set();
    sortedItems.forEach(item => {
      item.values?.forEach(v => subBrandSet.add(v.subBrandName));
    });

    const subBrands = Array.from(subBrandSet);

    // Build Series (color mapped)
    // ----------------------------
    const series = subBrands.map(name => ({
      label: name,
      color: getSubBrandColor(name)
    }));

    // Convert API → $K values
    // ----------------------------
    let actual = sortedItems.map(item => {
      return subBrands.map(brand => {
        const found = item.values?.find(v => v.subBrandName === brand);
        return found ? Number(found.reportedSalesAmount) / 1000 : 0; // $K scale

      });
    });

    // Detect Outlier (C4 spike)
    const allValues = actual.flat();
    const rawMax = Math.max(...allValues, 0);
    const visibleMax =
      rawMax > 100 ? rawMax * 0.25 : rawMax;   // dynamic compression

    actual = actual.map(row =>
      row.map(v => (v > visibleMax ? visibleMax : v))
    );

    const niceTickStep = (maxVal, tickCount = 5) => {
      if (maxVal === 0) return 1;

      const roughStep = maxVal / tickCount;
      const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
      const residual = roughStep / magnitude;

      let niceResidual;
      if (residual >= 5) niceResidual = 5;
      else if (residual >= 2) niceResidual = 2;
      else niceResidual = 1;

      return niceResidual * magnitude;
    };

    const tickStep = niceTickStep(visibleMax, 5);
    const maxTick = tickStep * Math.ceil(visibleMax / tickStep);

    const yTicks = [];
    for (let t = 0; t <= maxTick + tickStep / 2; t += tickStep) {
      yTicks.push(Number(t.toFixed(2)));
    }

    return {
      title: "Reported Dollars Sold by Sub-Brand",
      xLabels,
      series,
      dataMatrix: { actual },
      yTicks
    };

  }, [commercialData]);
  console.log(lineChartSubBrandData, "lineChartSubBrandDatas")

  const topSkuChartData = useMemo(() => {
    const items =
      commercialData?.top10SKUsByReportedDollarsSold?.items ?? [];

    const dataMatrixActual = items.map(item => [
      (item.currentMTD ?? 0) / 1_000_000,
      (item.lastMTD ?? 0) / 1_000_000
    ]);

    // Flatten all values to find max for x-axis ticks
    const allValues = dataMatrixActual.flat();
    const maxValue = Math.max(...allValues);

    // Create nice rounded ticks dynamically
    const tickStep = Math.ceil(maxValue / 5); // 5 ticks
    const xTicks =
      maxValue === 0
        ? [0]
        : Array.from({ length: 6 }, (_, i) => i * tickStep);

    return {
      title: "Top 10 Ordered SKUs by Reported Dollars Sold",
      xLabels: items.map(item => item.sku ?? "—"),
      series: [
        { label: "Dollars Sold - This Period", key: "thisPeriod", color: "#0A3478" },
        { label: "Dollars Sold - Last Period", key: "lastPeriod", color: "#A8C4EA" }
      ],
      dataMatrix: {
        actual: dataMatrixActual
      },
      xTicks //dynamically generated
    };
  }, [commercialData]);



  const bottom10SkuChartData = useMemo(() => {
    const items =
      commercialData?.bottom10SKUsByReportedDollarsSold?.items ?? [];

    const sortedItems = [...items]
      .sort((a, b) => (a.currentMTD ?? 0) - (b.currentMTD ?? 0))
      .slice(0, 10);

    const dataMatrixActual = sortedItems.map(item => [
      (item.currentMTD ?? 0) / 1_000_000,
      (item.lastMTD ?? 0) / 1_000_000
    ]);

    // Flatten all values to find max for x-axis ticks
    const allValues = dataMatrixActual.flat();
    const maxValue = Math.max(...allValues);

    // Create nice rounded ticks dynamically
    const tickStep = Math.ceil(maxValue / 5); // 5 ticks
    const xTicks =
      maxValue === 0
        ? [0]
        : Array.from({ length: 6 }, (_, i) => i * tickStep);

    return {
      title: "Bottom 10 Ordered SKUs by Reported Dollars Sold ($M)",
      xLabels: sortedItems.map(item => item.sku ?? "—"),
      series: [
        { label: "Dollars Sold - This Period", key: "thisPeriod", color: "#0A3478" },
        { label: "Dollars Sold - Last Period", key: "lastPeriod", color: "#A8C4EA" }
      ],
      dataMatrix: {
        actual: dataMatrixActual
      },
      xTicks //dynamically generated
    };
  }, [commercialData]);




  const brandSalesChartData = useMemo(() => {
    const items = commercialData?.reportedDollarSalesByBrandWithCompetitor?.items;

    if (!Array.isArray(items) || items.length === 0) return null;

    // ---- X Labels (Brand Names)
    const xLabels = items.map(item => item.brandName);

    // ---- Helper to convert to Millions (since chart shows $M)
    const toMillions = (val) => Number((Number(val || 0) / 1_000_000).toFixed(3));

    // ---- Build dataMatrix.actual  [lastYear, currentYear]
    const actual = items.map(item => [
      toMillions(item.lastYear),     // Last Period
      toMillions(item.currentYear),  // This Period
    ]);

    // ---- Series with special color handling
    const baseSeries = [
      { label: "Dollars Sold - Last Period", key: "last", color: "#8EB7DB" },
      { label: "Dollars Sold - This Period", key: "current", color: "#0A3A73" },
    ];

    // Override colors for specific brands (C4 & Bloom)
    const customSeriesOverrides = items.flatMap(item => {
      if (item.brandName === "C4") {
        return [
          { label: "C4 - Last Period", key: "last", color: "#D6C85F" },
          { label: "C4 - This Period", key: "current", color: "#EACF2F" },
        ];
      }

      if (item.brandName === "Bloom") {
        return [
          { label: "Bloom - Last Period", key: "last", color: "#D9C3E9" },
          { label: "Bloom - This Period", key: "current", color: "#C393E4" },
        ];
      }

      return [];
    });

    const series = [...baseSeries, ...customSeriesOverrides];

    return {
      title: "Reported Dollars Sold by Brand",
      xLabels,
      series,
      dataMatrix: {
        actual
      }
    };
  }, [commercialData]);
  console.log(brandSalesChartData, "brandSalesChartDatas")

  const brandPriceComparison = useMemo(() => {
    const items =
      commercialData?.basePriceVsPromotedPrice?.items ?? [];

    const brandMap = {};

    items.forEach(item => {
      const brand = item.brandName;
      if (!brand) return;

      if (!brandMap[brand]) {
        brandMap[brand] = {
          promoLast: [],
          baseLast: [],
          promoCurrent: [],
          baseCurrent: []
        };
      }

      if (item.avgPromoPriceMTDLast != null)
        brandMap[brand].promoLast.push(normalizePrice(item.avgPromoPriceMTDLast));

      if (item.basePriceMTDLast != null)
        brandMap[brand].baseLast.push(normalizePrice(item.basePriceMTDLast));

      // fallback to QTD/YTD for current
      const promoCurrent =
        item.avgPromoPriceMTDCurrent ??
        item.avgPromoPriceQTDCurrent ??
        item.avgPromoPriceYTDCurrent;

      const baseCurrent =
        item.basePriceMTDCurrent ??
        item.basePriceQTDCurrent ??
        item.basePriceYTDCurrent;

      if (promoCurrent != null)
        brandMap[brand].promoCurrent.push(normalizePrice(promoCurrent));

      if (baseCurrent != null)
        brandMap[brand].baseCurrent.push(normalizePrice(baseCurrent));
    });

    const avg = arr =>
      arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

    const brands = Object.keys(brandMap);
    const dataMatrix = {
      actual: brands.map(brand => {
        const b = brandMap[brand];
        return [
          avg(b.promoLast),
          avg(b.baseLast),
          avg(b.promoCurrent),
          avg(b.baseCurrent)
        ];
      })
    }
    const allValues = dataMatrix?.actual?.flat();
    const maxValue = Math.max(...allValues);

    // Avoid zero issue
    let yTicks = [0];

    if (maxValue > 0) {
      const rawStep = maxValue / 5;

      const magnitude = Math.pow(
        10,
        Math.floor(Math.log10(rawStep))
      );

      const niceStep = Math.ceil(rawStep / magnitude) * magnitude;

      yTicks = Array.from({ length: 6 }, (_, i) =>
        Number((i * niceStep).toFixed(2))
      );

    }
    console.log(yTicks, "%%%%")
    return {
      title: "Brand Base Price vs Promoted Price",

      xLabels: brands,

      series: [
        { label: "Average Promo Price - Last Period", color: "#8C8C8C" },
        { label: "Base Price - Last Period", color: "#D8C72C" },
        { label: "Average Promo Price - This Period", color: "#000000" },
        { label: "Base Price - This Period", color: "#FFE000" }
      ],


      dataMatrix,
      yTicks
    };
  }, [commercialData]);

  const [hover, setHover] = React.useState(false);



  const stateNameToCode = {
    Alabama: "AL",
    Alaska: "AK",
    Arizona: "AZ",
    Arkansas: "AR",
    California: "CA",
    Colorado: "CO",
    Connecticut: "CT",
    Delaware: "DE",
    Florida: "FL",
    Georgia: "GA",
    Hawaii: "HI",
    Idaho: "ID",
    Illinois: "IL",
    Indiana: "IN",
    Iowa: "IA",
    Kansas: "KS",
    Kentucky: "KY",
    Louisiana: "LA",
    Maine: "ME",
    Maryland: "MD",
    Massachusetts: "MA",
    Michigan: "MI",
    Minnesota: "MN",
    Mississippi: "MS",
    Missouri: "MO",
    Montana: "MT",
    Nebraska: "NE",
    Nevada: "NV",
    New_Hampshire: "NH",
    New_Jersey: "NJ",
    New_Mexico: "NM",
    New_York: "NY",
    North_Carolina: "NC",
    North_Dakota: "ND",
    Ohio: "OH",
    Oklahoma: "OK",
    Oregon: "OR",
    Pennsylvania: "PA",
    Rhode_Island: "RI",
    South_Carolina: "SC",
    South_Dakota: "SD",
    Tennessee: "TN",
    Texas: "TX",
    Utah: "UT",
    Vermont: "VT",
    Virginia: "VA",
    Washington: "WA",
    West_Virginia: "WV",
    Wisconsin: "WI",
    Wyoming: "WY",
  };
  const thickBorderStates = [
    "CA", "AZ", "NM", "TX", "OK", "MO", "IL",
    "IN", "KY", "TN", "GA", "NC", "VA"
  ];
  const legendData = [
    { label: "$0–$5M", color: "#C8C6C4", min: 0, max: 5000000 },
    { label: "$5M–$10M", color: "#D0EBBE", min: 5000000, max: 10000000 },
    { label: "$10M–$20M", color: "#B7E29A", min: 10000000, max: 20000000 },
    { label: "$20M–$35M", color: "#99D56F", min: 20000000, max: 35000000 },
    { label: "$35M–$50M", color: "#6BB542", min: 35000000, max: 50000000 },
    { label: "$50M+", color: "#4C8C17", min: 50000000, max: Infinity },
  ];
  const usemapData = useMemo(() => {
    const items = commercialData?.reportedDollarSoldByState?.items;

    const usaData = {};
    if (Array.isArray(items)) {
      items.forEach(item => {
        const stateCode = stateNameToCode[item.stateName] || item.stateName;
        usaData[stateCode] = item.ytdValue ?? 0;
      });
    }

    return {
      usaData, // will at least be an empty object
      thickBorderStates,
      legendData,
    };
  }, [commercialData]);

  console.log(usemapData, "usedata")

  return (

    <Box sx={{
      ml: 0,
      transition: "margin-left 0.25s ease",
      "& .MuiBox-root": { backgroundImage: "none" }
    }} style={{
      padding: isMobile ? '8px' : '0px  16px 0px 16px',
      fontFamily: 'inter !important'
    }}>
      {/* Loading Backdrop */}
      <Backdrop
        open={loading}
        sx={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      <Box
        sx={{
          height: {
            xs: "calc(100dvh - 140px)", // mobile header
            md: "calc(100vh - 64px)",  // desktop header
          },
          overflowY: "auto",
          // px: isMobile ? 1 : 2,
          pb: "100px",
          /* smooth scrolling */
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch", // iOS momentum

          /* hide scrollbar */
          scrollbarWidth: "none",          // Firefox
          msOverflowStyle: "none",         // IE / Edge
          "&::-webkit-scrollbar": {
            display: "none",               // Chrome / Safari
          },
        }}
      >
        {/* Section Title: KPI's */}
        <Typography sx={{ mb: 1, mt: 3 }}>
          <span style={{ color: '#1672B980', fontSize: '16px', fontWeight: 200 }}>Commercial / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>KPI's</span>
        </Typography>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <KpiMetricscards cardsData={kpicardsdata} kpiBriefData={kpiBriefData} />

        {/* Section Title: Time Series */}
        <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
          <span style={{ color: '#1672B980', fontSize: '16px', fontWeight: 200 }}>Commercial / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>Time Series</span>
        </Typography>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />

        {/* Section Title: Highlights with YTD/QTD/MTD buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 3 }}>
          <Typography sx={{ fontSize: '16px' }}>
            <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Commercial / </span><span style={{ fontWeight: 200, fontSize: "16px" }}> Highlights</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['YTD', 'QTD', 'MTD'].map((label, idx) => (
              <FilterChip
                key={label}
                label={label}
                idx={idx}
                activeIdx={highlightsActiveIdx}
                onClick={handleHighlightsChipClick}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        <HighlightsCards data={highlightCards} />

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontSize: '16px' }}>
              <span style={{ color: "#1672B980", fontSize: '16px', fontWeight: 200 }}>Commercial / </span> <span style={{ fontWeight: 200, fontSize: "16px" }}>Visualizations</span>
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {["YTD", "QTD", "MTD"].map((label, idx) => (
                <FilterChip
                  key={label}
                  label={label}
                  idx={idx}
                  activeIdx={visualizationActiveIdx}
                  onClick={handleVisualizationChipClick}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
        </Box>
        {/* Section Title: Visualizations */}
        <Grid
          container
          rowSpacing={2}
          columnSpacing={2}
          sx={{
            mt: "26px !important",
            alignItems: "stretch"   // keeps all cards same height
          }}
        >
          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
          }} >
            <PieLegendCard title="Reported Dollar Sold by Brand" data={brandsData} />
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }} >
            <PieLegendCard title="Reported Dollars Sold by Sub Brand" data={subbrandsData} />
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }}  >
            <PieLegendCard title="Brand Marketshare by Dollars Sold" data={pieDataCompetitors} />
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{
            pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
          }} >
            <PieLegendCard title="Reported Dollar Sales by Region" data={pieDataRegion} />
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mt: "10px !important" }}>
          {/* LEFT CARD - USA MAP */}
          <Grid item xs={12} md={4} >
            <Map title="Reported Dollars Sold Heatmap by State" usaData={usemapData}
              thickBorderStates={thickBorderStates} legendData={legendData} />

          </Grid>

          {/* RIGHT CARD - BAR CHART */}
          <Grid item xs={12} md={8} >
            {regionChartData && (
              <Barcharts
                title={regionChartData.title}
                xLabels={regionChartData.xLabels}
                series={regionChartData.series}
                dataMatrix={regionChartData.dataMatrix}
                yTicks={regionChartData.yTicks}
                rounded={true}
                yAxisLabel="Net Sales ($M)"
              />
            )}
          </Grid>
        </Grid>
        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            {lineChartSubBrandData && (
              <LineChartSubBrand
                title={lineChartSubBrandData.title}
                xLabels={lineChartSubBrandData.xLabels}
                series={lineChartSubBrandData.series}
                dataMatrix={lineChartSubBrandData.dataMatrix}
                yTicks={lineChartSubBrandData.yTicks}
                valueFormatter={(v) => `$${v}M`}
                yAxisLabel="Reported Dollars Sold ($M)"
              />
            )}
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mt: '34px !important' }}>
          {/* LEFT CARD - USA MAP */}
          <Grid item xs={12} md={6} sx={{
            pt: { xs: "0px !important", sm: "0px !important", }
          }}>
            <TopSkuBarChart
              title={topSkuChartData.title}
              xLabels={topSkuChartData.xLabels}
              series={topSkuChartData.series}
              dataMatrix={topSkuChartData.dataMatrix}
              xTicks={topSkuChartData.xTicks}
            />

          </Grid>

          {/* RIGHT CARD - BAR CHART */}
          <Grid item xs={12} md={6} sx={{
            pt: { xs: "34px !important", sm: "34px !important", md: '0px !important' }
          }}>
            <TopSkuBarChart
              title={bottom10SkuChartData.title}
              xLabels={bottom10SkuChartData.xLabels}
              series={bottom10SkuChartData.series}
              dataMatrix={bottom10SkuChartData.dataMatrix}
              xTicks={bottom10SkuChartData.xTicks}
            />

          </Grid>
        </Grid>

        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            {brandSalesChartData && (
              <DoubleBarChart
                title={brandSalesChartData.title}
                xLabels={brandSalesChartData.xLabels}
                series={brandSalesChartData.series}
                dataMatrix={brandSalesChartData.dataMatrix}
                yTicks={brandSalesChartData.yTicks}
              />
            )}

          </Grid>
        </Grid>

        <Grid container sx={{ mt: "34px !important" }}>
          <Grid item xs={12}>
            <BrandPriceDumbbellChart
              title={brandPriceComparison.title}
              xLabels={brandPriceComparison.xLabels}
              series={brandPriceComparison.series}
              dataMatrix={brandPriceComparison.dataMatrix}
              yTicks={brandPriceComparison.yTicks}
            />
          </Grid>
        </Grid>

      </Box>


      {/* <Visualizations /> */}
    </Box>


  );
};

export default CommercialDashboard;